import { useLocalStorageState } from 'ahooks';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RouterPaths } from '../../components/Router/Router.constants.ts';
import { tLayoutContext } from './Layout.types.ts';
import { tLeadMessageEvent } from '../SocketsProvider/Sockets.types.ts';
import { useSocketsContext } from '../SocketsProvider/Sockets.context.ts';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { shouldNotifyLeadMessage } from './Layout.functions';

const useLayout = (): tLayoutContext => {
  const [sidebarOpen, setSidebarOpen] = useLocalStorageState<
    boolean | undefined
  >('sidebarOpen', {
    listenStorageChange: true,
    defaultValue: undefined,
  });
  const [openAvatarMenu, setOpenAvatarMenu] = useState<boolean>(false);
  const [messageNotification, setMessageNotification] =
    useState<tLeadMessageEvent | null>(null);
  const { leadMessageEvent } = useSocketsContext();
  const { currentUser } = useUserContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const [currentTheme, setCurrentTheme] = useLocalStorageState<
    string | undefined
  >('leadsales');
  const location = useLocation();
  const pageSectionPadding =
    location.pathname === RouterPaths.chat ? 'px-4' : 'px-6';

  function toggleSidebar() {
    setSidebarOpen(!sidebarOpen);
  }

  useEffect(() => {
    const resolved =
      currentTheme === 'darkLeadsales' ? 'dark' : currentTheme;
    if (resolved !== currentTheme) setCurrentTheme(resolved);
    if (resolved) {
      document.documentElement.setAttribute('data-theme', resolved);
    }
  }, [currentTheme]);

  // Only the toast and its sound are gated: seeing a chat is not the same as being
  // interrupted by it (BUG-894). The board subscribes to the same emitter, ungated.
  leadMessageEvent.useSubscription((event) => {
    if (
      !shouldNotifyLeadMessage({
        event,
        currentWorkspaceId,
        currentUserUuid: currentUser?.uuid,
      })
    ) {
      return;
    }
    setMessageNotification(event);
  });

  return {
    sidebarOpen,
    openAvatarMenu,
    pageSectionPadding,
    setSidebarOpen,
    setOpenAvatarMenu,
    toggleSidebar,
    currentTheme,
    setCurrentTheme,
    messageNotification,
    setMessageNotification,
  };
};

export default useLayout;
